import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import {
  Facebook, Instagram, Heart, MessageCircle, Share2, Send,
  Bookmark, MoreHorizontal, ThumbsUp, Film, Image as ImageIcon
} from 'lucide-react';
import TikTokIcon from '../icons/TikTokIcon';

const FBReaction = () => (
  <div className="flex items-center gap-1">
    <div className="flex -space-x-1">
      <div className="w-[18px] h-[18px] rounded-full bg-blue-500 flex items-center justify-center"><ThumbsUp className="w-2.5 h-2.5 text-white" /></div>
      <div className="w-[18px] h-[18px] rounded-full bg-red-500 flex items-center justify-center"><Heart className="w-2.5 h-2.5 text-white" /></div>
    </div>
    <span className="text-xs text-gray-500">0</span>
  </div>
);

// ─── Media Renderer ───
const MediaPreview = ({ mediaUrl, mediaType, isVideo, apiBase, aspectClass = "aspect-square" }) => {
  if (!mediaUrl) {
    return (
      <div className={`${aspectClass} bg-gray-100 flex items-center justify-center`}>
        <ImageIcon className="w-12 h-12 text-gray-300" />
      </div>
    );
  }
  const fullUrl = mediaUrl.startsWith('http') ? mediaUrl : `${apiBase}${mediaUrl}`;
  if (isVideo) {
    return (
      <div className={`${aspectClass} bg-black relative overflow-hidden`}>
        <video
          src={fullUrl}
          className="w-full h-full object-cover"
          muted
          autoPlay
          loop
          playsInline
        />
        <Badge className="absolute top-2 right-2 bg-black/60 text-white text-[10px]">Video</Badge>
      </div>
    );
  }
  return <img src={fullUrl} alt="" className={`w-full ${aspectClass} object-cover`} />;
};

// ─── Facebook Post Mockup ───
const FacebookPostPreview = ({ content, mediaUrl, isVideo, pageName, format, apiBase }) => (
  <div className="bg-white rounded-lg border shadow-sm overflow-hidden max-w-[360px] mx-auto" data-testid="fb-preview">
    {/* Header */}
    <div className="flex items-center gap-2 p-3">
      <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
        <Facebook className="w-5 h-5 text-white" />
      </div>
      <div className="flex-1">
        <p className="text-sm font-semibold leading-tight">{pageName || 'La tua Pagina'}</p>
        <p className="text-[11px] text-gray-500">Adesso · <span className="text-gray-400">🌍</span></p>
      </div>
      <MoreHorizontal className="w-5 h-5 text-gray-400" />
    </div>
    {/* Content */}
    {content && format !== 'reel' && (
      <div className="px-3 pb-2">
        <p className="text-sm whitespace-pre-wrap leading-snug">{content}</p>
      </div>
    )}
    {/* Media */}
    {format === 'story' ? (
      <div className="relative">
        <MediaPreview mediaUrl={mediaUrl} isVideo={isVideo} apiBase={apiBase} aspectClass="aspect-[9/16] max-h-[400px]" />
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center ring-2 ring-blue-400">
            <Facebook className="w-4 h-4 text-white" />
          </div>
          <span className="text-white text-xs font-semibold drop-shadow">{pageName || 'La tua Pagina'}</span>
        </div>
      </div>
    ) : format === 'reel' ? (
      <div className="relative">
        <MediaPreview mediaUrl={mediaUrl} isVideo={true} apiBase={apiBase} aspectClass="aspect-[9/16] max-h-[400px]" />
        <div className="absolute bottom-3 left-3 right-12">
          <p className="text-white text-xs font-semibold drop-shadow mb-1">{pageName || 'La tua Pagina'}</p>
          {content && <p className="text-white text-[11px] drop-shadow line-clamp-2">{content}</p>}
        </div>
        <div className="absolute bottom-3 right-3 flex flex-col items-center gap-3">
          <Heart className="w-5 h-5 text-white drop-shadow" />
          <MessageCircle className="w-5 h-5 text-white drop-shadow" />
          <Share2 className="w-5 h-5 text-white drop-shadow" />
        </div>
      </div>
    ) : (
      <MediaPreview mediaUrl={mediaUrl} isVideo={isVideo} apiBase={apiBase} aspectClass="aspect-square" />
    )}
    {/* Actions (only for regular posts) */}
    {format === 'post' && (
      <>
        <div className="px-3 py-1.5 flex items-center justify-between">
          <FBReaction />
          <span className="text-xs text-gray-500">0 commenti</span>
        </div>
        <div className="border-t mx-3" />
        <div className="flex items-center justify-around py-1.5 px-3">
          <button className="flex items-center gap-1.5 text-gray-500 text-xs font-medium py-1.5 px-3 rounded hover:bg-gray-50"><ThumbsUp className="w-4 h-4" />Mi piace</button>
          <button className="flex items-center gap-1.5 text-gray-500 text-xs font-medium py-1.5 px-3 rounded hover:bg-gray-50"><MessageCircle className="w-4 h-4" />Commenta</button>
          <button className="flex items-center gap-1.5 text-gray-500 text-xs font-medium py-1.5 px-3 rounded hover:bg-gray-50"><Share2 className="w-4 h-4" />Condividi</button>
        </div>
      </>
    )}
  </div>
);

// ─── Instagram Post Mockup ───
const InstagramPostPreview = ({ content, mediaUrl, isVideo, username, format, apiBase }) => (
  <div className="bg-white rounded-lg border shadow-sm overflow-hidden max-w-[360px] mx-auto" data-testid="ig-preview">
    {format === 'story' ? (
      <div className="relative">
        <MediaPreview mediaUrl={mediaUrl} isVideo={isVideo} apiBase={apiBase} aspectClass="aspect-[9/16] max-h-[500px]" />
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center ring-2 ring-pink-300">
            <Instagram className="w-4 h-4 text-white" />
          </div>
          <span className="text-white text-xs font-semibold drop-shadow">{username || 'il_tuo_profilo'}</span>
        </div>
        {/* Story progress bar */}
        <div className="absolute top-1 left-2 right-2 h-0.5 bg-white/30 rounded-full">
          <div className="h-full w-1/3 bg-white rounded-full" />
        </div>
      </div>
    ) : format === 'reel' ? (
      <div className="relative">
        <MediaPreview mediaUrl={mediaUrl} isVideo={true} apiBase={apiBase} aspectClass="aspect-[9/16] max-h-[500px]" />
        <div className="absolute bottom-3 left-3 right-12">
          <p className="text-white text-xs font-semibold drop-shadow mb-1">{username || 'il_tuo_profilo'}</p>
          {content && <p className="text-white text-[11px] drop-shadow line-clamp-2">{content}</p>}
        </div>
        <div className="absolute bottom-3 right-3 flex flex-col items-center gap-4">
          <Heart className="w-5 h-5 text-white drop-shadow" />
          <MessageCircle className="w-5 h-5 text-white drop-shadow" />
          <Send className="w-5 h-5 text-white drop-shadow" />
          <Bookmark className="w-5 h-5 text-white drop-shadow" />
        </div>
        {/* Reel badge */}
        <div className="absolute top-3 right-3">
          <Badge className="bg-black/50 text-white text-[10px]">Reel</Badge>
        </div>
      </div>
    ) : (
      <>
        {/* Header */}
        <div className="flex items-center gap-2 p-3">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Instagram className="w-4 h-4 text-white" />
          </div>
          <p className="text-sm font-semibold flex-1">{username || 'il_tuo_profilo'}</p>
          <MoreHorizontal className="w-5 h-5 text-gray-400" />
        </div>
        {/* Media */}
        <MediaPreview mediaUrl={mediaUrl} isVideo={isVideo} apiBase={apiBase} aspectClass="aspect-square" />
        {/* Actions */}
        <div className="flex items-center justify-between px-3 py-2">
          <div className="flex items-center gap-4">
            <Heart className="w-6 h-6" />
            <MessageCircle className="w-6 h-6" />
            <Send className="w-6 h-6" />
          </div>
          <Bookmark className="w-6 h-6" />
        </div>
        {/* Caption */}
        {content && (
          <div className="px-3 pb-3">
            <p className="text-sm"><span className="font-semibold mr-1">{username || 'il_tuo_profilo'}</span>{content}</p>
          </div>
        )}
      </>
    )}
  </div>
);

// ─── TikTok Post Mockup ───
const TikTokPostPreview = ({ content, mediaUrl, isVideo, username, apiBase }) => (
  <div className="bg-black rounded-lg overflow-hidden max-w-[360px] mx-auto relative" data-testid="tiktok-preview">
    <MediaPreview mediaUrl={mediaUrl} isVideo={true} apiBase={apiBase} aspectClass="aspect-[9/16] max-h-[500px]" />
    <div className="absolute bottom-3 left-3 right-14">
      <p className="text-white text-sm font-bold drop-shadow mb-1">@{username || 'il_tuo_profilo'}</p>
      {content && <p className="text-white text-xs drop-shadow line-clamp-3">{content}</p>}
    </div>
    <div className="absolute bottom-3 right-3 flex flex-col items-center gap-4">
      <Heart className="w-6 h-6 text-white drop-shadow" />
      <MessageCircle className="w-6 h-6 text-white drop-shadow" />
      <Bookmark className="w-6 h-6 text-white drop-shadow" />
      <Share2 className="w-6 h-6 text-white drop-shadow" />
    </div>
  </div>
);

// ─── Main Preview Dialog ───
const PostPreviewDialog = ({ open, onClose, post, apiBase }) => {
  const platforms = post?.platforms || [];
  const content = post?.content || '';
  const format = post?.post_format || 'post';
  const mediaUrls = post?.media_urls || [];
  const mediaUrl = mediaUrls[0] ? (typeof mediaUrls[0] === 'string' ? mediaUrls[0] : mediaUrls[0].url) : null;
  const isVideo = post?.media_types?.[0] === 'video'
    || (typeof mediaUrls[0] !== 'string' && mediaUrls[0]?.media_type === 'video')
    || (mediaUrl && mediaUrl.match(/\.(mp4|mov|webm|avi)$/i));

  const defaultTab = platforms.includes('facebook') ? 'facebook'
    : platforms.includes('instagram') ? 'instagram'
    : platforms.includes('tiktok') ? 'tiktok' : 'facebook';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto p-0">
        <DialogHeader className="p-4 pb-0">
          <DialogTitle className="font-outfit text-base">Anteprima Post</DialogTitle>
        </DialogHeader>
        <Tabs defaultValue={defaultTab} className="w-full">
          <TabsList className="mx-4 grid" style={{ gridTemplateColumns: `repeat(${platforms.length || 1}, 1fr)` }}>
            {platforms.includes('facebook') && (
              <TabsTrigger value="facebook" className="gap-1.5 text-xs">
                <Facebook className="w-3.5 h-3.5" />Facebook
              </TabsTrigger>
            )}
            {platforms.includes('instagram') && (
              <TabsTrigger value="instagram" className="gap-1.5 text-xs">
                <Instagram className="w-3.5 h-3.5" />Instagram
              </TabsTrigger>
            )}
            {platforms.includes('tiktok') && (
              <TabsTrigger value="tiktok" className="gap-1.5 text-xs">
                <TikTokIcon className="w-3.5 h-3.5" />TikTok
              </TabsTrigger>
            )}
          </TabsList>

          <div className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Badge variant="outline" className="text-[10px] capitalize">{format}</Badge>
              {mediaUrl && <Badge variant="outline" className="text-[10px]">{isVideo ? 'Video' : 'Immagine'}</Badge>}
            </div>

            {platforms.includes('facebook') && (
              <TabsContent value="facebook" className="mt-0">
                <FacebookPostPreview
                  content={content} mediaUrl={mediaUrl} isVideo={isVideo}
                  pageName={post?.page_name} format={format} apiBase={apiBase}
                />
              </TabsContent>
            )}
            {platforms.includes('instagram') && (
              <TabsContent value="instagram" className="mt-0">
                <InstagramPostPreview
                  content={content} mediaUrl={mediaUrl} isVideo={isVideo}
                  username={post?.ig_username} format={format} apiBase={apiBase}
                />
              </TabsContent>
            )}
            {platforms.includes('tiktok') && (
              <TabsContent value="tiktok" className="mt-0">
                <TikTokPostPreview
                  content={content} mediaUrl={mediaUrl} isVideo={isVideo}
                  username={post?.ig_username} apiBase={apiBase}
                />
              </TabsContent>
            )}
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default PostPreviewDialog;
